import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

const problems = [
  { num: '01', title: '情報はある', desc: '活動記録も、実績データも、スタッフの声も、十分に蓄積されている。' },
  { num: '02', title: '実績もある', desc: '長年にわたる地道な取り組みが、確かな成果として積み重なっている。' },
  { num: '03', title: '努力もある', desc: '現場は誠実に、真剣に、社会のために動き続けている。' },
  { num: '04', title: 'それでも届かない', desc: '支持者が増えない。連携が生まれない。なぜか。', highlight: true },
];

export default function ProblemSection() {
  const heading = useScrollAnimation();
  const grid = useStaggerAnimation(problems.length);
  const footer = useScrollAnimation();

  return (
    <section className="texture-problem py-28 md:py-40 bg-warm-white relative overflow-hidden">

      {/* ── 「断絶・散乱・未接続」をテーマにした装飾 ── */}

      {/* 左側縦ライン（セクションラベル） */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3">
        <span className="block w-px h-10 bg-divider" />
        <span className="text-[10px] font-garamond italic text-muted-light tracking-widest-jp" style={{ writingMode: 'vertical-rl' }}>Problem</span>
        <span className="block w-px h-10 bg-divider" />
      </div>

      {/* 右下の大きな同心円（届かない＝広がらない波紋） */}
      <div className="absolute -bottom-20 -right-20 w-[220px] h-[220px] lg:w-[480px] lg:h-[480px] rounded-full border border-accent-amber/18 pointer-events-none" />
      <div className="absolute -bottom-28 -right-28 w-[320px] h-[320px] lg:w-[640px] lg:h-[640px] rounded-full border border-accent-gold/12 pointer-events-none hidden sm:block" />
      <div className="absolute -bottom-36 -right-36 w-[420px] h-[420px] lg:w-[800px] lg:h-[800px] rounded-full border border-accent-amber/7 pointer-events-none hidden md:block" />
      {/* 波紋の中心点（右下）— 届かない発信源 */}
      <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-accent-amber/30 pointer-events-none hidden lg:block" />
      <div className="absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full bg-accent-amber/50 pointer-events-none hidden lg:block" />

      {/* 右上の「断絶した菱形」— 繋がっていない断片 */}
      {/* 外側の菱形（不完全・一辺だけ） */}
      <div
        className="absolute top-8 right-8 lg:top-14 lg:right-18 w-10 h-10 lg:w-20 lg:h-20 pointer-events-none"
        style={{
          borderTop: '2px solid rgba(212,122,46,0.38)',
          borderRight: '2px solid rgba(212,122,46,0.38)',
          transform: 'rotate(45deg)',
        }}
      />
      <div
        className="absolute top-12 right-12 lg:top-22 lg:right-26 w-5 h-5 lg:w-10 lg:h-10 pointer-events-none"
        style={{
          borderBottom: '1.5px solid rgba(201,168,76,0.45)',
          borderLeft: '1.5px solid rgba(201,168,76,0.45)',
          transform: 'rotate(45deg)',
        }}
      />
      {/* 孤立した小さな点 */}
      <div className="absolute top-6 right-20 lg:top-10 lg:right-40 w-2 h-2 rounded-full bg-accent-amber/35 pointer-events-none" />
      <div className="absolute top-20 right-6 lg:top-36 lg:right-12 w-1.5 h-1.5 rounded-full bg-accent-gold/40 pointer-events-none" />

      {/* ドットグリッド（右上）— 散らばった情報 */}
      <div className="absolute top-0 right-0 w-36 h-36 lg:w-72 lg:h-72 pointer-events-none overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(212,122,46,0.22) 1.5px, transparent 1.5px)', backgroundSize: '18px 18px' }} />
      </div>

      {/* 左上の「散乱した断片」— 情報が散らばっている */}
      <div className="absolute top-16 left-16 pointer-events-none hidden lg:block">
        {/* バラバラに配置された小さな要素 */}
        <div className="absolute w-8 h-px bg-accent-teal/25" style={{ top: 0, left: 0, transform: 'rotate(15deg)' }} />
        <div className="absolute w-5 h-px bg-accent-amber/30" style={{ top: 12, left: 20, transform: 'rotate(-25deg)' }} />
        <div className="absolute w-6 h-px bg-accent-gold/25" style={{ top: 24, left: 5, transform: 'rotate(40deg)' }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-teal/35" style={{ top: 8, left: 40 }} />
        <div className="absolute w-1 h-1 rounded-full bg-accent-amber/40" style={{ top: 28, left: 30 }} />
        <div className="absolute w-2 h-2 bg-accent-gold/25" style={{ top: 18, left: 55, transform: 'rotate(20deg)' }} />
        <div className="absolute w-1 h-1 rounded-full bg-accent-teal/30" style={{ top: 36, left: 15 }} />
        <div className="absolute w-3 h-px bg-accent-amber/25" style={{ top: 40, left: 45, transform: 'rotate(-10deg)' }} />
      </div>

      {/* 中央左の「未接続の線」— 繋がっていない */}
      <div className="absolute left-1/4 top-1/3 pointer-events-none hidden lg:block">
        <div className="absolute w-px h-12 bg-gradient-to-b from-transparent via-accent-amber/20 to-transparent" style={{ left: 0, top: 0 }} />
        <div className="absolute w-px h-8 bg-gradient-to-b from-transparent via-accent-teal/18 to-transparent" style={{ left: 16, top: 20 }} />
        <div className="absolute w-px h-10 bg-gradient-to-b from-transparent via-accent-gold/15 to-transparent" style={{ left: 32, top: 5 }} />
        {/* 繋がっていない端点 */}
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-amber/30" style={{ left: -1, top: -2 }} />
        <div className="absolute w-1 h-1 rounded-full bg-accent-teal/25" style={{ left: 15, top: 18 }} />
        <div className="absolute w-1.5 h-1.5 rounded-full bg-accent-gold/28" style={{ left: 31, top: 3 }} />
      </div>

      {/* 右中央の「途切れた格子」— 構造が不完全 */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:block">
        {[0, 1, 2, 3].map(row => (
          <div key={row} className="flex gap-3 mb-3">
            {[0, 1, 2, 3, 4].map(col => (
              <div
                key={col}
                className="w-1 h-1 rounded-full"
                style={{
                  backgroundColor: (row + col) % 3 === 0
                    ? 'rgba(212,122,46,0.35)'
                    : (row + col) % 3 === 1
                    ? 'rgba(201,168,76,0.25)'
                    : 'rgba(74,158,142,0.20)',
                  opacity: (row * 5 + col) % 4 === 0 ? 0.3 : 1,
                }}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        {/* Heading */}
        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 transition-all duration-700"
          style={{ opacity: heading.isVisible ? 1 : 0, transform: heading.isVisible ? 'translateY(0)' : 'translateY(32px)' }}
        >
          <div className="md:col-span-7 md:col-start-3">
            <div className="flex items-center gap-4 mb-8">
              <span className="block w-6 h-px bg-accent-teal" />
              <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Problem</span>
            </div>
            <h2 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-3xl md:text-4xl">
              なぜ、価値は伝わらないのか。
            </h2>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
          <div
            ref={grid.ref as React.RefObject<HTMLDivElement>}
            className="md:col-span-10 md:col-start-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-divider">
              {problems.map((p, i) => (
                <div
                  key={p.num}
                  className={`border-b border-r border-divider p-10 md:p-12 relative group transition-all duration-600 ${p.highlight ? 'bg-accent-amber-light' : 'hover:bg-warm-light'}`}
                  style={{
                    opacity: grid.visibleCount > i ? 1 : 0,
                    transform: grid.visibleCount > i ? 'translateY(0)' : 'translateY(24px)',
                    transitionDelay: `${i * 80}ms`,
                  }}
                >
                  <span className={`absolute top-6 right-8 font-garamond text-6xl font-light leading-none select-none transition-all duration-300 ${p.highlight ? 'text-accent-amber/25' : 'text-divider/60 group-hover:text-divider'}`}>
                    {p.num}
                  </span>
                  <h3 className={`font-serif-jp font-light tracking-normal-jp mb-5 leading-tight text-xl ${p.highlight ? 'text-accent-amber' : 'text-charcoal'}`}>
                    {p.title}
                  </h3>
                  <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{p.desc}</p>
                </div>
              ))}
            </div>

            {/* Footer text */}
            <div
              ref={footer.ref as React.RefObject<HTMLDivElement>}
              className="mt-12 pl-0 md:pl-2 transition-all duration-700"
              style={{ opacity: footer.isVisible ? 1 : 0, transform: footer.isVisible ? 'translateY(0)' : 'translateY(20px)' }}
            >
              <div className="flex items-start gap-5">
                <span className="block w-1.5 h-1.5 rounded-full bg-accent-amber flex-shrink-0 mt-2" />
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl">
                  問題は、活動の質ではありません。<br />
                  「意味」が言語化されていないこと。「価値」が広告やデザインに落とし込まれていないこと。<br />
                  それが、届かない本当の理由です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
